/**
 * NFR-11 — "text must stay readable against its background in both themes.
 * Any bar or chip that carries white text must be dark enough for it in both
 * themes. One schedule bar in the prototype writes fixed white text on a
 * colour supplied by code, which is the exact way this goes wrong."
 *
 * This build's instance of that exact defect was the asset-category initials
 * chip (`assets-page.tsx`): fixed `text-white` on `asset.categoryColor`, a
 * colour that comes from data — and even the SEEDED palette fails it (white
 * on amber #f59e0b is ~2.2:1 against NFR-40's 4.5:1 floor).
 *
 * The fix is to stop asserting white and COMPUTE the ink: WCAG relative
 * luminance of the supplied colour, then whichever of the two theme inks
 * (near-black `#111827`, white) contrasts harder. A computed choice cannot go
 * stale when an administrator picks a new colour, which is what "supplied by
 * code" means. The chosen ink pair is theme-independent because the
 * BACKGROUND it sits on is the supplied colour itself, not a theme surface —
 * the same pair is right in light and dark.
 */

/** WCAG 2.x relative luminance of a #rrggbb / #rgb colour; null if unparseable. */
export function relativeLuminance(hex: string): number | null {
  const match = /^#(?:([0-9a-f]{6})|([0-9a-f]{3}))$/i.exec(hex.trim());
  if (!match) return null;
  const six = match[1] ?? match[2]!.split('').map((c) => c + c).join('');
  const channel = (i: number): number => {
    const srgb = parseInt(six.slice(i * 2, i * 2 + 2), 16) / 255;
    return srgb <= 0.04045 ? srgb / 12.92 : Math.pow((srgb + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(0) + 0.7152 * channel(1) + 0.0722 * channel(2);
}

const DARK_INK = '#111827';
const DARK_INK_LUMINANCE = relativeLuminance(DARK_INK)!;

/**
 * The ink to write on a data-supplied background colour: whichever of white
 * and near-black contrasts harder (WCAG contrast ratio). An unparseable
 * colour gets white, matching the schema default `#64748b` it stands in for.
 */
export function readableTextOn(background: string): string {
  const l = relativeLuminance(background);
  if (l === null) return '#ffffff';
  const contrastWithWhite = 1.05 / (l + 0.05);
  const contrastWithDark = (l + 0.05) / (DARK_INK_LUMINANCE + 0.05);
  return contrastWithWhite >= contrastWithDark ? '#ffffff' : DARK_INK;
}
